import {css, StyleSheet} from 'aphrodite';
import classNames from 'classnames';
import React, {RefObject, useEffect, useRef} from 'react';

import HelpButton from 'help/HelpButton';
import getTestProps from 'test/lib/cypress/getTestProps';
import DataTestAttrs from 'test/lib/cypress/shared/DataTestAttrs';
import {BLUE, GRAY_DARK} from 'ui-library/Colors';
import {FloatingInteraction} from 'ui-library/floating-ui/FloatingConstants';
import {LabelStyle} from 'ui-library/inputs/InputConstants';
import {InputChangeEvent} from 'util/Types';

import FieldLabel from './FieldLabel';

export interface CheckboxInputProps {
  checked: boolean;
  className?: string;
  // id is required for now due to the htmlFor implementation detail, but in the future we could wrap the
  // and text into a <label> to avoid the need for that.
  id: string;
  indeterminate?: boolean;
  inputRef?: RefObject<HTMLInputElement>;
  label: string | JSX.Element;
  labelStyle?: LabelStyle;
  helpText?: string;
  disabled?: boolean;
  onChange: (e: InputChangeEvent) => void;
  dataTestAttrs?: DataTestAttrs;
}

function CheckboxInput(props: CheckboxInputProps): JSX.Element {
  // You can't conditionally use hooks, so we always do a useRef and just don't use it if we're passed a
  // ref in props.
  const internalRef = useRef<HTMLInputElement | null>(null);
  const ref = props.inputRef ?? internalRef;
  useEffect(() => {
    if (ref.current != null && props.indeterminate != null) {
      ref.current.indeterminate = props.indeterminate;
    }
  }, [ref, props.indeterminate]);

  return (
    <div className={classNames(props.className, css(styles.checkboxInputContainer))}>
      <input
        className={css(styles.checkbox)}
        checked={props.checked}
        id={props.id}
        onChange={props.onChange}
        disabled={props.disabled}
        type="checkbox"
        ref={ref}
        {...getTestProps(props.dataTestAttrs)}
      />
      <FieldLabel
        className={classNames('u-mini-spacing-left', 'checkboxInput-label', {
          [css(styles.checkboxLabelDisabled)]: props.disabled,
        })}
        htmlFor={props.id}
        label={props.label}
        labelStyle={props.labelStyle}
      />
      {props.helpText && (
        <HelpButton
          renderContent={() => <p>{props.helpText}</p>}
          iconClassName={classNames('u-mini-spacing-left', css(styles.helpButton))}
          interaction={FloatingInteraction.HOVER}
        />
      )}
    </div>
  );
}

CheckboxInput.defaultProps = {
  disabled: false,
};

const styles = StyleSheet.create({
  checkbox: {
    accentColor: BLUE,
    verticalAlign: 'text-top',
  },
  checkboxInputContainer: {
    display: 'flex',
    alignItems: 'flex-start',
  },
  checkboxLabelDisabled: {
    color: GRAY_DARK,
  },
  helpButton: {
    verticalAlign: 'text-top',
  },
});

export default CheckboxInput;
